import React, { useState, useEffect } from "react";
import { Select, Button, message } from "antd";
import {
  ArrowRightOutlined,
  FileTextOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getData,postData } from "../../config/api-connect";
import { getColumnSearchProps, getColumnFilters } from "../../utils/filters";
import SummaryLayout from "../../components/layout/SummaryLayout";
import MonthSelector from "../../components/ui/MonthSelector";
import { useMonthFilter } from "../../hooks/useMonthFilter";
import { formatCurrency } from "../../utils/helper";

const { Option } = Select;

function GstSummary() {
  const navigate = useNavigate();
  const { selectedMonth, setSelectedMonth } = useMonthFilter();

  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(() => {
    const saved = localStorage.getItem('selectedCity');
    return saved ? JSON.parse(saved) : "";
  });
  const [summaryData, setSummaryData] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    getData("/finance/get_cities_and_venues")
      .then((response) => {
        setCities(response.cities || []);
      })
      .catch((error) => {
        console.error("Error fetching cities:", error.message);
      });
  }, []);

  useEffect(() => {
    getSummaryData();
  }, [selectedCityId, selectedMonth.format("YYYY-MM")]);

  const getSummaryData = () => {
    setLoading(true);

    const month = selectedMonth.format("MM");
    const year = selectedMonth.format("YYYY");
    const cityQuery = selectedCityId ? `&city_id=${selectedCityId}` : "";

    getData(
      `/finance/get_venuewise_gst_summary/?month=${month}&year=${year}${cityQuery}`,
    )
      .then((response) => {
        setSummaryData(response.result || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching summary:", error.message);
      });
  };

  const generateInvoices = async () => {
    try {
      if (!selectedCityId) {
        message.warning("Please select a city");
        return;
      }

      setGenerating(true);

      const month = selectedMonth.format("MM");
      const year = selectedMonth.format("YYYY");

      const response = await postData(
        `/finance/generate_gst_invoices/?month=${month}&year=${year}&city_id=${selectedCityId}`
      );

      if (response.success) {
        message.success(
          response.message || "GST invoices generated successfully"
        );

        getSummaryData();
      } else {
        message.error(response.message || "Failed to generate invoices");
      }
    } catch (error) {
      message.error("Something went wrong");
      setGenerating(false);
    } 
  };

  const clickedARow = (record) => {
    navigate("/gst/invoices", { state: { venue_id: record.venue_id } });
  };

  const uniqueValues = (key) =>
    Array.from(new Set(summaryData.map((r) => r[key]).filter(Boolean))).sort();

  const columns = [
    {
      title: "Venue",
      dataIndex: "venue_name",
      align: "left",
      ...getColumnFilters("venue_name", uniqueValues("venue_name")),
    },
    {
      title: "Number of Invoices",
      dataIndex: "invoice_count",
      align: "center",
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap'} }),
      sorter: (a, b) =>
        (Number(a.invoice_count) || 0) - (Number(b.invoice_count) || 0),
    },
    {
      title: "Invoice Number Range",
      dataIndex: "invoice_range",
      align: "center",
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap'} }),
      ...getColumnSearchProps("invoice_range"),
    },
    {
      title: "Total Collection (Incl. Tax)",
      dataIndex: "total_collection",
      align: "right",
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap'} }),
      sorter: (a, b) =>
        (Number(a.total_collection) || 0) - (Number(b.total_collection) || 0),
      render: (val) =>
        val == null ? "—" : formatCurrency(val),
    },
    {
      title: "Total Tax",
      dataIndex: "total_tax",
      align: "right",
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap'} }),
      sorter: (a, b) => (Number(a.total_tax) || 0) - (Number(b.total_tax) || 0),
      render: (val) =>
        val == null ? "—" : formatCurrency(val),
    },
    {
      title: "",
      dataIndex: "more",
      key: "more",
      fixed: "right",
      render: (item, record) => (
        <a style={{ color: "blue" }} onClick={() => clickedARow(record)}>
          <ArrowRightOutlined style={{ color: "#1565c0" }} />
        </a>
      ),
    },
  ];

  const chooseCity = (value) => {
    setSelectedCityId(value);
    localStorage.setItem("selectedCity", JSON.stringify(value));
  };

  const renderRightSideActions = () => (
    <>
      <Select
        showSearch
        placeholder="Select City"
        style={{ width: 170, marginRight: 8 }}
        value={selectedCityId}
        onChange={(value) => chooseCity(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        {cities.map((city) => (
          <Option key={city.id} value={city.id}>
            {city.name}
          </Option>
        ))}
      </Select>

      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />

      <Button
        type="primary"
        disabled={generating}
        onClick={generateInvoices}
      >
        Generate GST Invoices
      </Button>
    </>
  );

  const totalInvoices = summaryData.reduce(
    (acc, curr) => acc + (Number(curr.invoice_count) || 0),
    0,
  );
  const totalCollection = summaryData.reduce(
    (acc, curr) => acc + (Number(curr.total_collection) || 0),
    0,
  );
  const totalTax = summaryData.reduce(
    (acc, curr) => acc + (Number(curr.total_tax) || 0),
    0,
  );

  const statsConfig = [
    {
      key: "s_invoices",
      title: "Total Invoices",
      icon: <FileTextOutlined style={{ fontSize: 35, color: "#1890ff" }} />,
    },
    {
      key: "s_collection",
      title: "Total Collection (Incl. Tax)",
      icon: <BankOutlined style={{ fontSize: 35, color: "#52c41a" }} />,
    },
    {
      key: "s_tax",
      title: "Total Tax",
      icon: (
        <SafetyCertificateOutlined style={{ fontSize: 35, color: "#faad14" }} />
      ),
    },
  ];

  const totals = {
    s_invoices: totalInvoices,
    s_collection: `₹ ${totalCollection.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`,
    s_tax: `₹ ${totalTax.toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`,
  };

  return (
    <>
      <SummaryLayout
        title="GST Summary"
        headerContent={renderRightSideActions()}
        statsConfig={statsConfig}
        totals={totals}
        statsLoading={loading}
        tableProps={{
          rowKey: "venue_id",
          columns,
          data: summaryData,
          loading: loading,
          pagination: { pageSize: 20, showSizeChanger: false },
        }}
      />
    </>
  );
}

export default GstSummary;
